import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ExpiringTrainingCard } from '@/pages/org/ExpiringTrainingCard';
import {
  Users,
  BookOpen,
  Award,
  TrendingUp,
  ArrowRight,
  CreditCard,
  AlertCircle,
  Gift,
} from 'lucide-react';

const OrgAdminDashboard = () => {
  const { user, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [teamEnrollments, setTeamEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, orgRes, enrollmentsRes] = await Promise.all([
        api.get('/settings/dashboard-stats'),
        api.get('/organisations/my'),
        api.get('/enrollments/all?limit=10'),
      ]);
      setStats(statsRes.data);
      setOrganisation(orgRes.data);
      setTeamEnrollments(enrollmentsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['org_admin']}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    {
      title: 'Team Members',
      value: `${stats?.team_members || 0} / ${stats?.max_team_members || 5}`,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Team Enrollments',
      value: stats?.team_enrollments || 0,
      icon: BookOpen,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Completions',
      value: stats?.team_completions || 0,
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Certificates',
      value: stats?.team_certificates || 0,
      icon: Award,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
  ];

  return (
    <DashboardLayout requiredRoles={['org_admin']}>
      <div className="space-y-8" data-testid="org-admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">
              Organisation Dashboard
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-slate-600">{organisation?.name}</span>
              <Badge className={getStatusBadge(stats?.org_status)}>
                {stats?.org_status}
              </Badge>
            </div>
          </div>
          <div className="flex gap-3">
            <Button asChild variant="outline">
              <Link to="/organisation/team">Manage Team</Link>
            </Button>
            <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7]">
              <Link to="/organisation/reports">View Reports</Link>
            </Button>
          </div>
        </div>

        {/* Free Trial Access Banner - only for is_free_access orgs */}
        {organisation?.is_free_access && (
          <Card className="border-emerald-200 bg-emerald-50" data-testid="free-access-banner">
            <CardContent className="p-4 flex items-center gap-4">
              <Gift className="h-6 w-6 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-800">
                  Free Trial Access — No payment required
                </p>
                <p className="text-sm text-emerald-700">
                  Your organisation has complimentary access to all courses, features, and unlimited staff accounts.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Approval Warning */}
        {stats?.org_status === 'pending' && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-yellow-800">
                  Your organisation is pending approval
                </p>
                <p className="text-sm text-yellow-700">
                  Once approved, you'll be able to manage team members and enrollments.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">
                        {stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <ExpiringTrainingCard />

        {/* Team Seats */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Team Seats</CardTitle>
            <Badge variant="secondary" className="capitalize">
              {stats?.subscription_tier || 'Free'} Plan
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Seats Used</span>
                  <span className="font-medium">
                    {stats?.team_members || 0} of {stats?.max_team_members || 5}
                  </span>
                </div>
                <Progress
                  value={((stats?.team_members || 0) / (stats?.max_team_members || 5)) * 100}
                  className="h-3"
                />
              </div>
              <div className="flex gap-3">
                <Button asChild variant="outline" className="flex-1">
                  <Link to="/organisation/team">
                    <Users className="mr-2 h-4 w-4" />
                    Add Members
                  </Link>
                </Button>
                <Button asChild className="flex-1 bg-[#20acf6] hover:bg-[#3bb8f7]">
                  <Link to="/organisation/subscription">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Upgrade Plan
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Progress */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Team Progress</CardTitle>
            <Button variant="ghost" asChild>
              <Link to="/organisation/reports">
                View all <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {teamEnrollments.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-4">No team enrollments yet.</p>
                <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7]">
                  <Link to="/courses">Browse Courses</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {teamEnrollments.slice(0, 5).map((enrollment) => (
                  <div
                    key={enrollment.id}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900">
                        {enrollment.course_title}
                      </h4>
                      <div className="flex items-center gap-2 mt-2">
                        <Progress
                          value={enrollment.progress_percentage}
                          className="h-2 flex-1"
                        />
                        <span className="text-sm text-slate-600 w-12 text-right">
                          {Math.round(enrollment.progress_percentage)}%
                        </span>
                      </div>
                    </div>
                    <Badge
                      className={
                        enrollment.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }
                    >
                      {enrollment.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default OrgAdminDashboard;
